import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField, Select, MenuItem, Snackbar, Alert } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { PageHeader, FilterChips, ConfirmDialog, OrderDetailDialog, SkeletonCards, EmptyState, ErrorState, PermissionState, FeatureUsageMeter } from '../ui';
import ClientOrderRow from './ClientOrderRow';
import useClientOrdersPage from './useClientOrdersPage';
import { radius } from '../../themes/smartinventoryTheme';

const AdminOrders = () => {
  const { i18n } = useTranslation();
  const p = useClientOrdersPage();
  const showList = p.effectiveStatus === 'ready' && p.effectiveRows.length > 0;

  const statusOptions = [
    { value: 'all', label: p.t('All Statuses') },
    { value: 'pending', label: p.t('Pending') },
    { value: 'accepted', label: p.t('Accepted') },
    { value: 'paid', label: p.t('Paid') },
    { value: 'rejected', label: p.t('Rejected') },
  ];

  return (
    <Box>
      <PageHeader
        title={p.t('Client Orders', { defaultValue: 'Client orders' })}
        subtitle={p.t('{{count}} orders', { count: p.total, defaultValue: `${p.total} orders` })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search by Order #, Product Name') }}
        helpKey="client-orders"
        meter={<FeatureUsageMeter feature="orders.monthly" label={p.t('Orders this month', { defaultValue: 'Orders this month' })} />}
      />

      <Box data-tour="orders-filters" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <FilterChips options={statusOptions} value={p.statusFilter} onChange={p.setStatusFilter} filtersLabel={p.t('Filters')} />
        <Select
          value={p.sortBy}
          onChange={(e) => p.setSortBy(e.target.value)}
          size="small"
          sx={{ ml: 'auto', borderRadius: `${radius.pill}px` }}
        >
          <MenuItem value="newest">{p.t('Newest First')}</MenuItem>
          <MenuItem value="oldest">{p.t('Oldest First')}</MenuItem>
          <MenuItem value="price_high">{p.t('Price (High to Low)')}</MenuItem>
          <MenuItem value="price_low">{p.t('Price (Low to High)')}</MenuItem>
        </Select>
      </Box>

      <Box sx={{ border: showList ? '1px solid' : 'none', borderColor: 'divider', borderRadius: `${radius.xl}px`, overflow: 'hidden', bgcolor: showList ? 'background.paper' : 'transparent' }}>
        {p.effectiveStatus === 'loading' && <SkeletonCards />}

        {p.effectiveStatus === 'error' && (
          <ErrorState
            scope="page"
            title={p.t("Couldn't load orders", { defaultValue: "Couldn't load orders" })}
            description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
            request={p.errorInfo?.request}
            retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
            onRetry={p.fetchOrders}
          />
        )}

        {p.effectiveStatus === 'permission' && (
          <PermissionState
            title={p.t("You don't have access to Client Orders", { defaultValue: "You don't have access to Client Orders" })}
            description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
          />
        )}

        {p.effectiveStatus === 'ready' && p.effectiveRows.length === 0 && (
          p.isNoMatch ? (
            <EmptyState
              kind="no-match"
              title={p.t('No products match "{{query}}"', { query: p.search, defaultValue: 'No orders match your filters' })}
              description={p.t('Try a different search or clear the status filter.', { defaultValue: 'Try a different search or clear the status filter.' })}
              actions={[{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); p.setStatusFilter('all'); } }]}
            />
          ) : (
            <EmptyState
              kind="none"
              icon={ReceiptLongIcon}
              title={p.t('No orders found matching your criteria.')}
              description={p.t('Orders placed by your customers will show up here.', { defaultValue: 'Orders placed by your customers will show up here.' })}
            />
          )
        )}

        {showList && p.effectiveRows.map((order, index) => (
          <ClientOrderRow
            key={order._id}
            t={p.t}
            i18n={i18n}
            order={order}
            tourAnchor={index === 0}
            onView={p.setDetailOrder}
            onAccept={p.openAcceptDialog}
            onReject={p.openRejectDialog}
            onMarkPaid={(o) => p.openPaymentDialog(o, true)}
            onMarkUnpaid={(o) => p.openPaymentDialog(o, false)}
            paymentPending={p.paymentPendingId === order._id}
          />
        ))}
      </Box>

      {p.remaining > 0 && showList && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={p.loadMore} disabled={p.loadingMore} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('Load {{count}} more · {{remaining}} remaining', {
              count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
            })}
          </Button>
        </Box>
      )}

      <OrderDetailDialog
        order={p.detailOrder}
        open={Boolean(p.detailOrder)}
        onClose={() => p.setDetailOrder(null)}
        t={p.t}
        i18n={i18n}
      />

      {/* Reject — requires a reason, preset chips fill the box and stay editable */}
      <ConfirmDialog
        open={p.rejectOpen}
        onClose={() => p.setRejectOpen(false)}
        tone="danger"
        title={p.t('Reject order #{{orderNumber}}', { orderNumber: p.rejectTarget?.orderNumber, defaultValue: `Reject order #${p.rejectTarget?.orderNumber}` })}
        description={p.t('Give a reason. The customer sees this on their order.', { defaultValue: 'Give a reason. The customer sees this on their order.' })}
        cancelLabel={p.t('Back')}
        confirmLabel={p.t('Reject Order')}
        onConfirm={p.handleReject}
        loading={p.rejecting}
        confirmDisabled={p.rejectReason.trim() === ''}
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          autoFocus
          value={p.rejectReason}
          onChange={(e) => p.setRejectReason(e.target.value)}
          placeholder={p.t('Please explain why you are rejecting this order...')}
          error={p.rejectReason.trim() === ''}
          helperText={p.rejectReason.trim() === '' ? p.t('Rejection reason is required') : ''}
          sx={{ mt: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          {p.rejectPresets.map((preset) => (
            <Button key={preset} size="small" variant="outlined" onClick={() => p.setRejectReason(preset)} sx={{ borderRadius: `${radius.pill}px` }}>
              {preset}
            </Button>
          ))}
        </Box>
      </ConfirmDialog>

      {/* Accept — requires an ETA, quick presets fill the picker */}
      <ConfirmDialog
        open={p.acceptOpen}
        onClose={() => p.setAcceptOpen(false)}
        tone="primary"
        title={p.t('Accept order #{{orderNumber}}', { orderNumber: p.acceptTarget?.orderNumber, defaultValue: `Accept order #${p.acceptTarget?.orderNumber}` })}
        description={p.t('An ETA is required before accepting.', { defaultValue: 'An ETA is required before accepting.' })}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Accept & set ETA', { defaultValue: 'Accept & set ETA' })}
        onConfirm={p.handleAccept}
        loading={p.accepting}
        confirmDisabled={!p.acceptEta}
      >
        <TextField
          fullWidth
          type="datetime-local"
          label={p.t('Set ETA')}
          InputLabelProps={{ shrink: true }}
          value={p.acceptEta}
          onChange={(e) => p.setAcceptEta(e.target.value)}
          sx={{ mt: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={() => p.applyQuickEta('tomorrowAm')} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('Tomorrow AM', { defaultValue: 'Tomorrow AM' })}
          </Button>
          <Button size="small" variant="outlined" onClick={() => p.applyQuickEta('in2days')} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('In 2 days', { defaultValue: 'In 2 days' })}
          </Button>
          <Button size="small" variant="outlined" onClick={() => p.applyQuickEta('nextMonday')} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('Next Monday', { defaultValue: 'Next Monday' })}
          </Button>
        </Box>
      </ConfirmDialog>

      {/* Mark paid / unpaid */}
      <ConfirmDialog
        open={p.paymentDialog.open}
        onClose={p.closePaymentDialog}
        tone="primary"
        title={p.paymentDialog.targetPaid
          ? p.t('Mark order {{orderNumber}} as paid?', { orderNumber: p.paymentDialog.order?.orderNumber })
          : p.t('Mark order {{orderNumber}} as unpaid? The order will return to Accepted.', { orderNumber: p.paymentDialog.order?.orderNumber })}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Confirm')}
        onConfirm={p.handleConfirmPayment}
        loading={p.paymentPendingId === p.paymentDialog.order?._id}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminOrders;
